import { autoPlace, snapTimebox } from "@founderdeck/core";
import { useMemo, useState } from "react";
import { CapacityBar } from "../components/CapacityBar";
import { DateNav } from "../components/DateNav";
import { DayGrid } from "../components/DayGrid";
import { Mits } from "../components/Mits";
import { TaskRow } from "../components/TaskRow";
import { useApp } from "../app/AppContext";
import { useSession } from "../lib/auth-client";
import type { Task } from "../lib/api";
import { relDayLabel, todayKey } from "../lib/date";
import { useProjects, useTaskMutations, useTasks } from "../lib/queries";
import { isStale, mitsForDate, projectMap } from "../lib/tasks";

export function PlanPage() {
  const app = useApp();
  const { data: session } = useSession();
  const tasksQ = useTasks();
  const projectsQ = useProjects();
  const { update, remove } = useTaskMutations();

  const all = tasksQ.data ?? [];
  const projects = projectsQ.data ?? [];
  const pmap = useMemo(() => projectMap(projects), [projects]);
  const today = todayKey();
  // The day being planned. Defaults to today; the navigator moves it anywhere.
  const [planDate, setPlanDate] = useState(today);
  const isToday = planDate === today;
  const inFilter = (t: Task) => app.filter === "all" || t.projectId === app.filter;

  const dayTasks = all.filter((t) => t.doDate === planDate && inFilter(t));
  const mits = mitsForDate(all, planDate);
  const staleCount = all.filter(isStale).length;

  const dayList = dayTasks.slice().sort((a, b) => {
    const done = (a.status === "done" ? 1 : 0) - (b.status === "done" ? 1 : 0);
    if (done) return done;
    if (a.fromHour != null && b.fromHour != null) return a.fromHour - b.fromHour;
    if (a.fromHour != null) return -1;
    if (b.fromHour != null) return 1;
    return a.pri - b.pri;
  });

  const toggle = (t: Task) =>
    update.mutate({ id: t.id, patch: { status: t.status === "done" ? "todo" : "done" } });
  // Star is a Top-3 *for the day being planned*: toggle on/off for planDate.
  const star = (t: Task) =>
    update.mutate({
      id: t.id,
      patch:
        t.mitDate === planDate
          ? { mit: false, mitDate: null }
          : { mit: true, mitDate: planDate },
    });
  const del = (t: Task) => remove.mutate(t.id);

  const schedule = (id: string, rawMin: number) => {
    const t = all.find((x) => x.id === id);
    if (!t) return;
    update.mutate({
      id,
      patch: { fromHour: snapTimebox(rawMin, t.estMin ?? 30), doDate: planDate },
    });
  };
  const unschedule = (id: string) => update.mutate({ id, patch: { fromHour: null } });
  const resize = (id: string, estMin: number) =>
    update.mutate({ id, patch: { estMin } });

  const planDay = () => {
    const open = dayTasks.filter((t) => t.status !== "done");
    const placements = autoPlace(
      open.map((t) => ({
        id: t.id,
        estMin: t.estMin,
        energy: t.energy,
        fromHour: t.fromHour,
        pri: t.pri,
      })),
    );
    for (const [id, fromHour] of Object.entries(placements)) {
      update.mutate({ id, patch: { fromHour, doDate: planDate } });
    }
  };

  // Sweep overdue tasks onto the day being planned (defaults to today).
  const rollStale = () => {
    for (const t of all.filter(isStale)) {
      update.mutate({ id: t.id, patch: { doDate: planDate, fromHour: null } });
    }
  };

  const firstName = session?.user?.name?.split(" ")[0] ?? "there";
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const colHeading = isToday ? "Today" : relDayLabel(planDate, today);

  if (tasksQ.isLoading) return <div className="empty">Loading your day…</div>;

  return (
    <>
      <h1 className="title">
        {greet}, {firstName}.
      </h1>

      <DateNav date={planDate} onChange={setPlanDate} />

      <Mits mits={mits} onToggle={toggle} />
      <CapacityBar tasks={dayTasks} />

      <div className="planbar">
        <button className="mini hot" onClick={planDay}>
          ☀ {isToday ? "Plan day" : "Plan this day"} (auto-place)
        </button>
        {staleCount > 0 && (
          <button className="mini" onClick={rollStale}>
            ↻ {staleCount} stale → {isToday ? "today" : "here"}
          </button>
        )}
      </div>

      <div className="plan-cols">
        <section className="col">
          <div className="h">{colHeading}</div>
          {dayList.length ? (
            dayList.map((t) => (
              <TaskRow
                key={t.id}
                task={t}
                projects={pmap}
                onToggle={toggle}
                onStar={star}
                onDelete={del}
                draggable
              />
            ))
          ) : (
            <div className="empty">
              {isToday
                ? "Nothing on today. Drag from the Backlog, or hit ☀ Plan day."
                : "Nothing planned for this day yet. Drag tasks from the Backlog."}
            </div>
          )}
        </section>

        <section className="col">
          <DayGrid
            tasks={dayTasks}
            projects={pmap}
            date={planDate}
            isToday={isToday}
            onSchedule={schedule}
            onUnschedule={unschedule}
            onToggle={toggle}
            onResize={resize}
          />
        </section>
      </div>
    </>
  );
}
